export type GenerationCreditRecoveryStatus =
  | "restored"
  | "already_restored"
  | "unconfirmed";

export interface GenerationCreditRecovery {
  status: GenerationCreditRecoveryStatus;
  restored: boolean;
  balance?: number;
}

export function parseGenerationCreditRecovery(
  value: unknown
): GenerationCreditRecovery | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const status = record.status;
  if (
    status !== "restored" &&
    status !== "already_restored" &&
    status !== "unconfirmed"
  ) {
    return undefined;
  }
  if (typeof record.restored !== "boolean") return undefined;
  if (
    (status === "unconfirmed" && record.restored) ||
    (status !== "unconfirmed" && !record.restored)
  ) {
    return undefined;
  }
  return {
    status,
    restored: record.restored,
    ...(typeof record.balance === "number" && {
      balance: record.balance,
    }),
  };
}

export function formatGenerationFailureMessage(
  errorMessage: string,
  creditRecovery?: GenerationCreditRecovery
): string {
  const base =
    errorMessage.trim() || "We couldn't create your preview. Please try again.";
  if (!creditRecovery) return base;

  if (creditRecovery.restored) {
    if (/\bcredit\b.{0,30}\brestor(?:ed|ation)\b/i.test(base)) return base;
    const balance =
      creditRecovery.balance !== undefined
        ? ` Your balance is now ${creditRecovery.balance} credit${
            creditRecovery.balance === 1 ? "" : "s"
          }.`
        : "";
    return `${base} Your generation credit was restored.${balance}`;
  }

  if (creditRecovery.status === "unconfirmed") {
    return `${base} We couldn't confirm that your credit was restored. Check your balance and contact support if the credit is missing.`;
  }

  return base;
}
